import React, { useCallback } from 'react';

import { Grid } from '@gogame/components';
import { useChildComponent } from '@gogame/hooks-ts';
import { dateFNS } from '@gogame/intl-ts';

import {
  costModelSchema,
  toISO,
} from 'scenes/Campaigns/components/CostModel/schema';
import { getCostModelDefaultEndDate } from 'scenes/Campaigns/components/CostModel/utils';
import { loadCostModels } from 'scenes/Campaigns/sources/fields';
import SpendAdjustmentDetails from 'scenes/SpendAdjustments/components/SpendAdjustmentDetails';

import {
  PERIOD_START_DATE,
  PERIOD_END_DATE,
  TYPE,
  PERIOD,
  PERIODS,
  ACTIVITY_TYPE,
  IS_SUPPORT_ACTIVITY_TYPES,
  DEFAULT_ACTIVITY_TYPE,
} from '../constants';
import { CostModelPicker } from './CostModelPicker';
import CostModelPreviewItem from './CostModelPreviewItem';

const EMPTY_MODELS = [];

const COST_MODEL_LIMIT = 50;

const defaultCanRemove = () => false;

export const CostModelPreviews = ({
  models = EMPTY_MODELS,
  canAddCostModel,
  onRemove,
  onAdd,
  onEdit,
  canRemove = defaultCanRemove,
  campaignId,
  CostModelFormCreate,
  CostModelFormEdit,
  platformName,
  fullWidthItem = false,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();

  const singleModel = models.some(({ type }) => type?.isSingle);

  const handleIsCostModelOptionDisabled = useCallback(
    (option) => {
      return models.length ? option.isSingle : false;
    },
    [models]
  );

  const handleAddCostModel = (type) => {
    openChildComponent(CostModelFormCreate, {
      initialValues: costModelSchema.cast(
        {
          [TYPE]: type,
          [PERIODS]: [
            {
              [PERIOD]: {
                [PERIOD_START_DATE]: toISO(dateFNS.startOfDay(new Date())),
                [PERIOD_END_DATE]: toISO(getCostModelDefaultEndDate(type)),
              },
              ...(type?.[IS_SUPPORT_ACTIVITY_TYPES]
                ? {
                    [ACTIVITY_TYPE]: type?.[DEFAULT_ACTIVITY_TYPE],
                  }
                : {}),
            },
          ],
        },
        { context: {} }
      ),
      campaignId,
      selectedEventsIds: models
        ?.filter(({ subType }) => subType)
        .map(({ subType }) => subType.id),
      onSubmit: onAdd,
    });
  };

  const handleEditCostModel = (index, initialModel) => {
    openChildComponent(CostModelFormEdit, {
      id: initialModel?.[TYPE]?.id,
      campaignId,
      initialValues: initialModel,
      selectedEventsIds: models
        ?.filter(({ subType }) => subType)
        .map(({ subType }) => subType.id),
      onSubmit: (model) => {
        return onEdit(index, model);
      },
    });
  };

  const handleOpenSpendAdjustmentDetails = (id) => {
    openChildComponent(SpendAdjustmentDetails, {
      id,
    });
  };

  return (
    <Grid container spacing={2}>
      {canAddCostModel ? (
        <Grid item xs={12}>
          <CostModelPicker
            models={models}
            disabled={singleModel || models.length >= COST_MODEL_LIMIT}
            onChange={handleAddCostModel}
            isOptionDisabled={handleIsCostModelOptionDisabled}
            source={loadCostModels}
            platformName={platformName}
          />
        </Grid>
      ) : null}

      <Grid container item spacing={3}>
        {models.map((costModel, i) => {
          return (
            <Grid item xs={fullWidthItem ? 12 : 6} key={i}>
              <CostModelPreviewItem
                model={costModel}
                index={i}
                onRemove={onRemove}
                onEdit={handleEditCostModel}
                removable={canRemove(costModel)}
                onOpenSpendAdjustmentDetails={handleOpenSpendAdjustmentDetails}
              />
            </Grid>
          );
        })}
      </Grid>
      {childComponent}
    </Grid>
  );
};
